/**
 * EnvoiArreteSigneModal — M17 (retour Manon, mi-parcours).
 *
 * « Un bouton d'envoi automatique au demandeur et, si besoin, à d'autres
 *   personnes agents ou admin de la mairie à choisir parmi l'annuaire —
 *   et non, après la signature. »
 *
 * Le bouton n'apparaît qu'une fois l'arrêté signé ; le serveur refuse de
 * toute façon un envoi avant signature (409). Le PDF signé part en pièce
 * jointe ; chaque envoi est tracé et l'historique s'affiche en bas.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Button, Alert, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { AlertCircle, Send, CheckCircle2 } from 'lucide-react';
import type { AgentAnnuaire } from '@roronoa-collectivites/shared-types';

type Prep = Awaited<ReturnType<typeof api.serviceTechnique.arretes.envoi>>;

export function EnvoiArreteSigneModal({
  arreteId, arreteNumero, onClose,
}: { arreteId: string; arreteNumero: string; onClose: () => void }) {
  const [prep, setPrep] = useState<Prep | null>(null);
  const [annuaire, setAnnuaire] = useState<AgentAnnuaire[]>([]);
  const [inclureDemandeurs, setInclureDemandeurs] = useState(true);
  const [emailDemandeur, setEmailDemandeur] = useState('');
  const [choisis, setChoisis] = useState<string[]>([]);
  const [filtre, setFiltre] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultat, setResultat] = useState<string | null>(null);

  const charger = async () => {
    try {
      const [p, a] = await Promise.all([
        api.serviceTechnique.arretes.envoi(arreteId),
        api.annuaireInterne.lister().catch(() => ({ agents: [] as AgentAnnuaire[] })),
      ]);
      setPrep(p);
      setAnnuaire(a.agents);
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Chargement impossible');
    }
  };
  useEffect(() => { void charger(); /* eslint-disable-next-line */ }, [arreteId]);

  const visibles = useMemo(() => {
    const f = filtre.trim().toLowerCase();
    return annuaire.filter((a) => !f || `${a.prenom} ${a.nom}`.toLowerCase().includes(f));
  }, [annuaire, filtre]);

  const aDemandeurs = (prep?.demandeurs.length ?? 0) > 0;
  const nbDest = (inclureDemandeurs && aDemandeurs ? prep!.demandeurs.length : 0)
    + (emailDemandeur.trim() ? 1 : 0) + choisis.length;

  async function envoyer() {
    setEnvoi(true); setError(null); setResultat(null);
    try {
      const r = await api.serviceTechnique.arretes.envoyer(arreteId, {
        inclureDemandeurs: inclureDemandeurs && aDemandeurs,
        emailDemandeur: emailDemandeur.trim() || null,
        destinataireIds: choisis,
        message: message.trim() || null,
      });
      setResultat(r.echecs === 0
        ? `Arrêté envoyé à ${r.envoyes} destinataire${r.envoyes > 1 ? 's' : ''}.`
        : `${r.envoyes} envoi(s) réussi(s), ${r.echecs} en échec — voir l'historique, vous pouvez relancer.`);
      setChoisis([]); setEmailDemandeur(''); setMessage('');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Envoi impossible');
    } finally { setEnvoi(false); }
  }

  return (
    <Dialog open onClose={onClose} title={`Envoyer l'arrêté ${arreteNumero}`} size="lg"
      description="Le PDF signé part en pièce jointe. Chaque envoi est tracé.">
      <div className="space-y-4">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {resultat && <Alert variant="success" icon={CheckCircle2}>{resultat}</Alert>}

        {!prep ? (
          <p className="text-sm text-ink-subtle">Chargement…</p>
        ) : !prep.envoyable ? (
          <Alert variant="warning" icon={AlertCircle}>
            L'arrêté ne peut être envoyé qu'une fois signé.
          </Alert>
        ) : (
          <>
            <fieldset>
              <legend className="mb-1 text-sm font-medium text-ink">Demandeur</legend>
              {aDemandeurs ? (
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" checked={inclureDemandeurs}
                    onChange={(e) => setInclureDemandeurs(e.target.checked)} className="mt-1" />
                  <span>
                    {prep.demandeurs.map((d) => d.nom ? `${d.nom} (${d.email})` : d.email).join(', ')}
                    <span className="block text-xs text-ink-muted">Retrouvé depuis le ou les signalements d'origine.</span>
                  </span>
                </label>
              ) : (
                <p className="text-xs text-ink-muted">Aucun demandeur connu (arrêté saisi à la main).</p>
              )}
              <Input className="mt-2" type="email" value={emailDemandeur} maxLength={255}
                aria-label="E-mail d'un demandeur extérieur"
                placeholder="E-mail d'un demandeur extérieur (facultatif)"
                onChange={(e) => setEmailDemandeur(e.target.value)} />
            </fieldset>

            <fieldset>
              <legend className="mb-1 text-sm font-medium text-ink">Ajouter depuis l'annuaire</legend>
              <Input value={filtre} onChange={(e) => setFiltre(e.target.value)}
                placeholder="Filtrer par nom…" aria-label="Filtrer l'annuaire" />
              <div className="mt-2 flex max-h-48 flex-wrap gap-2 overflow-y-auto">
                {visibles.map((a) => {
                  const choisi = choisis.includes(a.id);
                  return (
                    <button key={a.id} type="button" aria-pressed={choisi}
                      onClick={() => setChoisis((l) => (choisi ? l.filter((x) => x !== a.id) : [...l, a.id]))}
                      className={`inline-flex h-8 items-center rounded-r-pill border px-3 text-xs ${
                        choisi ? 'border-accent/50 bg-accent/10 font-medium text-ink' : 'border-border bg-surface/50 text-ink-muted'
                      }`}>
                      {a.prenom} {a.nom}
                    </button>
                  );
                })}
                {visibles.length === 0 && <p className="text-xs text-ink-muted">Personne ne correspond.</p>}
              </div>
            </fieldset>

            <label className="block text-sm">
              <span className="font-medium text-ink">Message (facultatif)</span>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={3}
                className="mt-1 w-full rounded-md border border-border bg-surface p-2 text-sm" />
            </label>

            <div className="flex justify-end">
              <Button variant="primary" disabled={envoi || nbDest === 0} onClick={() => void envoyer()}>
                <Send className="h-4 w-4" aria-hidden="true" />
                {envoi ? 'Envoi…' : `Envoyer${nbDest ? ` à ${nbDest} destinataire${nbDest > 1 ? 's' : ''}` : ''}`}
              </Button>
            </div>
          </>
        )}

        {prep && prep.historique.length > 0 && (
          <div className="border-t border-border/60 pt-3">
            <p className="mb-1 text-xs font-medium text-ink">Déjà envoyé</p>
            <ul className="space-y-0.5">
              {prep.historique.map((h, i) => (
                <li key={i} className="text-xs text-ink-muted">
                  {new Date(h.le).toLocaleString('fr-FR')} — {h.nom ?? h.email}
                  {h.qualite === 'demandeur' ? ' (demandeur)' : ''}
                  {h.envoye ? ' ✓' : ' ✗ échec'}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  );
}
